import type React from "react";
import { useCallback, useEffect } from "react";

import globals from "@/util/global-whitelist.mjs";
import { USE_META_AS_CTRL } from "@/util/use-hotkey.mjs";

export default function useKeypress(
  key: string | string[],
  action: (e: KeyboardEvent, isKeyUp: boolean) => void,
  deps: React.DependencyList = [],
  options = { listenToKeyUpEvents: false },
) {
  const onKeypress = deps ? useCallback(action, deps) : action;
  const onKeyup = options.listenToKeyUpEvents
    ? deps
      ? useCallback(action, deps)
      : action
    : null;

  useEffect(() => {
    if (!onKeypress) return;

    const keys = (Array.isArray(key) ? key : [key]).map((key) => {
      return USE_META_AS_CTRL && key === "Control" ? "Meta" : key;
    });

    function handler(e: KeyboardEvent) {
      if (keys.includes(e.key) || keys.includes(e.code)) {
        if (e.repeat) {
          e.preventDefault();
          return;
        }
        if (e.type === "keydown") {
          onKeypress(e, false);
        } else if (onKeyup && e.type === "keyup") {
          onKeyup(e, true);
        }
      }
    }

    globals.addEventListener("keydown", handler);

    if (onKeyup) {
      globals.addEventListener("keyup", handler);
    }

    return () => {
      globals.removeEventListener("keydown", handler);
      if (onKeyup) {
        globals.removeEventListener("keyup", handler);
      }
    };
  }, [key, onKeypress]);
}
